import type {RefObject} from "react";
import type {ThreeEvent} from "@react-three/fiber";
import * as THREE from "three";
import type {SidebarValues} from "../../App.tsx";

/**
 * Detects dragging the object.
 */
export default class MoveGestures {

    readonly onPointerDown: (e: ThreeEvent<PointerEvent>) => void;
    readonly onPointerMove: (e: ThreeEvent<PointerEvent>) => void;
    readonly onPointerUp: (e: ThreeEvent<PointerEvent>) => void;
    readonly onPointerOut: (e: ThreeEvent<PointerEvent>) => void;

    private readonly onMove: (delta: { dx?: number; dy?: number; dz?: number }) => void

    private downPositions: Map<number, { x: number; y: number }> = new Map()

    constructor(pointers: RefObject<Map<number, { x: number; y: number }>>,
                onMove: (delta: { dx?: number; dy?: number; dz?: number }) => void,
                setValues: SidebarValues
    ) {
        this.onMove = onMove

        // POINTER EVENTS @ trackpad, stylus, mouse, tablets, smartphones
        this.onPointerDown = (e: ThreeEvent<PointerEvent>) => {
            if (this.intersections(e) === 0) return;

            if (pointers.current.size === 1 && pointers.current.has(e.pointerId)) {
                const target = (e.target as Element | null);
                try { target?.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }

                this.downPositions.set(e.pointerId, {
                    x: e.clientX,
                    y: e.clientY
                })
            }
        }
        this.onPointerMove = (e: ThreeEvent<PointerEvent>) => {
            if (this.intersections(e) === 0) return;

            const downPos = this.downPositions.get(e.pointerId)
            if (downPos && pointers.current.size === 1 && pointers.current.has(e.pointerId)) {
                const prev = pointers.current.get(e.pointerId)
                if (!prev) return
                const dx = e.clientX - prev.x
                const dy = e.clientY - prev.y
                this.onMove({dx: dx, dy: -dy});
                setValues(prev => ({
                    ...prev,
                    "dx": dx,
                    "dy": -dy,
                    "action": "move",
                }));
                pointers.current.set(e.pointerId, {x: e.clientX, y: e.clientY});
                this.downPositions.set(e.pointerId, {x: e.clientX, y: e.clientY});
            }
        }
        this.onPointerUp = (e: ThreeEvent<PointerEvent>) => {
            const target = (e.target as Element | null);
            try { target?.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
            this.onMove({});
            this.downPositions.delete(e.pointerId);
        }
        this.onPointerOut = (e: ThreeEvent<PointerEvent>) => {
            try { (e.target as Element | null)?.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
            this.onMove({});
            this.downPositions.delete(e.pointerId);
        }

        // console.debug("moving gestures created")
    }

    intersections(e: THREE.Event & { intersections?: any[] }) {
        const hasIntersection = (e && e.intersections && e.intersections.length > 0);
        return hasIntersection ? e.intersections!.length : 0;
    }

    public dispose() {
        //console.trace("moving gestures dispose")
        // nothing to do
    }
}
